import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Divider, Stack, Text } from 'shell/components';
import { getCriterionQuestionIds, getQuizAnswerText, getQuizAutoGradedScore, getQuizResponse } from 'utils/quizGrading';
import { getQuizOverview } from 'utils/quizOverview';

const ResponseViewer = ({
    quiz,
    submission,
    selectedCriterionId,
    selectedFeedbackId,
    selectedQuestionId,
    feedback,
    tab,
}) => {
    const { t } = useTranslation('quiz');
    const overview = useMemo(() => getQuizOverview(quiz), [quiz]);
    const score = getQuizAutoGradedScore(quiz, submission);
    const [expandedIds, setExpandedIds] = useState(() => [overview.timelyQuestions[0]?.id]);
    const criterionId =
        tab === 'grades'
            ? selectedCriterionId
            : feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)?.criterion_id;
    const selectedIds = useMemo(
        () => (selectedQuestionId ? [selectedQuestionId] : getCriterionQuestionIds(quiz, criterionId)),
        [criterionId, quiz, selectedQuestionId]
    );

    useEffect(() => {
        if (!selectedIds.length) return;
        setExpandedIds((current) => [...new Set([...current, ...selectedIds])]);
        document
            .getElementById(`quiz-response-${selectedIds[0]}`)
            ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }, [selectedIds]);

    return (
        <Stack spacing={5} sx={{ p: 5 }}>
            <Stack spacing={3}>
                {overview.timelyQuestions.map((question) => {
                    const response = getQuizResponse(submission, question.id);
                    const answer = getQuizAnswerText(response?.answer);
                    const selected = selectedIds.includes(question.id);
                    return (
                        <Accordion
                            component={'article'}
                            key={question.id}
                            id={`quiz-response-${question.id}`}
                            expanded={expandedIds.includes(question.id)}
                            onChange={(_, expanded) =>
                                setExpandedIds((current) =>
                                    expanded ? [...current, question.id] : current.filter((id) => id !== question.id)
                                )
                            }
                            sx={(theme) => ({
                                p: 5,
                                borderRadius: '16px !important',
                                border: `${selected ? 2 : 1}px solid ${selected ? theme.palette.ensightful.warning[700] : theme.palette.ensightful.neutral[300]}`,
                                background: theme.palette.ensightful.shades[0],
                            })}
                        >
                            <AccordionSummary aria-controls={`quiz-answer-${question.id}`}>
                                <Stack direction={'row'} spacing={1} alignItems={'flex-start'} sx={{ minWidth: 0 }}>
                                    <Text weight={'medium'}>
                                        {t('grading.question-label', 'Q{{number}})', { number: question.position })}
                                    </Text>
                                    <Text
                                        component={'div'}
                                        rich
                                        weight={'medium'}
                                        sx={{ minWidth: 0, '& img': { maxWidth: '100%' } }}
                                    >
                                        {question.content ||
                                            t('create.question-missing-content', 'Question text is unavailable.')}
                                    </Text>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails id={`quiz-answer-${question.id}`}>
                                <Divider sx={{ my: 5 }} />
                                <Text weight={'medium'} sx={{ mb: 2 }}>
                                    {t('grading.student-response', 'Student Response')}
                                </Text>
                                <Text
                                    component={'div'}
                                    rich={typeof response?.answer === 'string' && /<[^>]+>/.test(answer)}
                                    sx={(theme) => ({
                                        p: 4,
                                        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
                                        borderRadius: '16px',
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        '& img': { maxWidth: '100%' },
                                    })}
                                >
                                    {answer || t('grading.no-response', 'No response provided.')}
                                </Text>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
            {overview.lmsQuestions.length > 0 && (
                <Stack spacing={3}>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={3}>
                        <Text color={'light'} weight={'medium'}>
                            {t('grading.auto-graded', 'Auto-Graded Questions')}
                        </Text>
                        <Text>{t('grading.points-total', '{{earned}}/{{possible}} pts total', score)}</Text>
                    </Stack>
                    {overview.lmsQuestions.map((question) => (
                        <Stack
                            component={'article'}
                            key={question.id}
                            direction={'row'}
                            justifyContent={'space-between'}
                            spacing={4}
                            sx={(theme) => ({
                                p: 5,
                                borderRadius: '16px',
                                background: theme.palette.ensightful.neutral[200],
                            })}
                        >
                            <Text component={'div'} rich color={'light'} sx={{ minWidth: 0 }}>
                                {question.content}
                            </Text>
                            <Text sx={{ flexShrink: 0 }}>
                                {t('grading.question-points', 'Pts: {{earned}} / {{possible}}', {
                                    earned: getQuizResponse(submission, question.id)?.score ?? '—',
                                    possible: question.points,
                                })}
                            </Text>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default ResponseViewer;
